import { useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { submitBlindBid } from "../../api/caseNegotiations";
import useErrorHandler from "../../hooks/useErrorHandler";
import useSnackbar from "../../hooks/useSnackbar";
import { SIDE_STYLE, fmtDateTime, toUSD } from "./negotiation-ui";

export default function BlindBidPanel({ caseId, negId, neg, viewer, blind, active, onChanged }) {
  const { data: user } = useSelector((state) => state.user);
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [busy, setBusy] = useState(false);
  const isTrial = /^trial_user$/i.test(user.roles);

  const rounds = blind?.rounds || [];
  const revealed = blind?.revealed || null;
  const maxRounds = blind?.maxRounds ?? neg.max_rounds;
  const canBid = Boolean(viewer?.side) && blind && !blind.mySubmitted && active && !isTrial;

  async function onSubmit(e) {
    e.preventDefault();
    const value = Number(amount);
    if (!(value > 0)) {
      setAmountError("Enter an amount greater than $0.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await submitBlindBid(user.token, caseId, negId, { amount: value });
      setAmount("");
      const msg = {
        SETTLED: "The figures matched. The negotiation is settled.",
        NO_MATCH: "No match this round. The next round is open.",
        ENDED: "The rounds ended without a match.",
      }[data?.resolved];
      snackbar.showSuccess(msg || "Your confidential bid was submitted");
    } catch (error) {
      errorHandler(error);
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  // revealed figures grouped per round
  const revealedRounds = {};
  (revealed || []).forEach((r) => {
    (revealedRounds[r.round] = revealedRounds[r.round] || []).push(r);
  });

  return (
    <Paper variant="outlined" className="mt-4 rounded-xl p-4">
      <Box className="flex flex-wrap items-center justify-between gap-2">
        <Box className="flex items-center gap-2">
          <LockOutlinedIcon fontSize="small" className="text-brand" />
          <Typography fontWeight={600}>Confidential bidding</Typography>
        </Box>
        {blind && active ? (
          <Chip size="small" variant="outlined" label={`Round ${blind.round} of ${maxRounds}`} className="!font-semibold" />
        ) : null}
      </Box>
      <Typography fontSize="small" color="text.secondary" className="mt-1">
        Bids are confidential. Each round the plaintiff and the defendant each submit one figure privately. If the
        defendant&apos;s figure reaches the plaintiff&apos;s, the negotiation settles at the midpoint of the two. Neither side sees the
        other&apos;s figure unless it settles.
      </Typography>

      {neg.status === "ENDED" ? (
        <Alert severity="warning" className="mt-3">
          The rounds ended without a match.
        </Alert>
      ) : null}

      {rounds.length ? (
        <Box className="mt-3 flex flex-wrap gap-2">
          {rounds.map((r) => (
            <Chip key={r.round} size="small" variant="outlined" label={`Round ${r.round}: no match`} className="!bg-[#f1f3f5] !text-[#555] !border-[#c8cdd2]" />
          ))}
        </Box>
      ) : null}

      {active && blind ? (
        <Box className="mt-3">
          {blind.deadline ? (
            <Typography fontSize="small" color="text.secondary" className="!mb-1">
              This round closes {fmtDateTime(blind.deadline)}.
            </Typography>
          ) : null}
          {viewer?.side && blind.mySubmitted ? (
            <Alert severity="info">
              Waiting for the other side. Your bid for round {blind.round} is in
              {blind.otherSubmitted ? "; the other side has also bid." : "; the other side has not bid yet."}
            </Alert>
          ) : null}
          {!viewer?.side ? (
            <Typography fontSize="small" color="text.secondary">
              Round {blind.round} is in progress. Figures stay hidden until the negotiation settles.
            </Typography>
          ) : null}
          {canBid ? (
            <Box component="form" onSubmit={onSubmit} noValidate className="flex flex-wrap items-start gap-2">
              <TextField
                type="number"
                size="small"
                label="Your confidential bid ($)"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setAmountError("");
                }}
                inputProps={{ min: 0, step: "any" }}
                error={Boolean(amountError)}
                helperText={amountError || (blind.otherSubmitted ? "The other side has already bid this round." : "")}
                className="w-[260px]"
              />
              <Button type="submit" variant="contained" size="large" disabled={busy} className="normal-case">
                Submit bid
              </Button>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {revealed && revealed.length ? (
        <Box className="mt-4">
          <Typography fontSize="small" fontWeight={600} color="text.secondary" className="!mb-1">
            Figures revealed after settlement
          </Typography>
          {Object.keys(revealedRounds).map((round) => (
            <Box key={round} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-0 border-t border-solid border-[#eef1f5] py-2">
              <Typography fontSize="small" fontWeight={600} className="w-[70px]">
                Round {round}
              </Typography>
              {revealedRounds[round].map((r) => {
                const style = SIDE_STYLE[r.side] || SIDE_STYLE.NEUTRAL;
                return (
                  <Box key={r.side} className="flex items-center gap-1">
                    <span className={`inline-block h-[10px] w-[10px] rounded-full ${style.dot}`} />
                    <Typography fontSize="small">
                      {style.label}: <b>{toUSD(r.amount)}</b>
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}
